import { makeStyles } from '@material-ui/styles'
import { IHealthServiceDetailsPageTabs } from 'app/plugins/openstack/components/networks/model'
import clsx from 'clsx'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import Text from 'core/elements/Text'
import colors from 'core/themes/base/colors'
import Theme from 'core/themes/model'
import { routeManager } from 'core/utils/routes'
import React, { useCallback } from 'react'
import useReactRouter from 'use-react-router'
import CardWithStatusBar from './CardWithStatusBar'
import { ServiceState } from './helper'

const useStyles = makeStyles<Theme>((theme) => ({
  container: {
    display: 'flex',
    alignItems: 'center',
    padding: 16,
    background: theme.components.card.background,
    border: `1px solid ${theme.components.card.border}`,
    borderRadius: 4,
    position: 'relative',
  },
  content: {
    display: 'grid',
    gap: '12px',
  },
  success: {
    background: `${colors.green[100]} !important`,
    color: `${colors.green[80]} !important`,
  },
  error: {
    background: `${colors.red[100]} !important`,
    color: `${colors.pink[800]} !important`,
  },
  warning: {
    background: `${colors.yellow[100]} !important`,
    color: `${colors.yellow[800]} !important`,
  },
  iconsContainer: {
    display: 'flex',
    gap: 16,
    alignItems: 'center',
  },
  iconWithCount: {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
    cursor: 'pointer',
  },
  iconContainer: {
    background: theme.palette.grey[100],
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 32,
    width: 32,
  },
  divider: {
    width: 1,
    height: 24,
    background: colors.grey[500],
  },
}))

export enum ServiceHealthHeaderType {
  HypervisorServices = 'HypervisorServices',
  HostServices = 'HostServices',
  ControlPlaneServices = 'ControlPlaneServices',
}

interface ServiceHealthHeaderCardProps {
  type: ServiceHealthHeaderType
  title: string
  statusDetails: ServiceState
}

const ServiceHealthHeaderCard = ({ type, title, statusDetails }: ServiceHealthHeaderCardProps) => {
  const { history } = useReactRouter()
  const classes = useStyles()
  const problematicCount =
    Number(statusDetails?.unhealthy ?? 0) + Number(statusDetails?.warning ?? 0)

  const handleClick = useCallback(() => {
    history.replace(
      routeManager.routes.openstack.health.details.path({
        tab:
          type === ServiceHealthHeaderType.ControlPlaneServices
            ? IHealthServiceDetailsPageTabs.ControlPlaneServicesHealth
            : type === ServiceHealthHeaderType.HostServices
            ? IHealthServiceDetailsPageTabs.HostHealth
            : IHealthServiceDetailsPageTabs.ServiceHealth,
      }),
    )
  }, [history, type])

  return (
    <CardWithStatusBar statusDetails={statusDetails}>
      <div className={classes.content}>
        <Text variant="subtitle2">
          {title} ({statusDetails?.total})
        </Text>
        <div className={classes.iconsContainer}>
          {/* Healthy */}
          <div className={classes.iconWithCount} onClick={handleClick}>
            <div className={clsx(classes.iconContainer, classes.success)}>
              <FontAwesomeIcon className={classes.icon}>circle-check</FontAwesomeIcon>
            </div>
            <Text variant="body2">{statusDetails?.healthy}</Text>
          </div>
          <div className={classes.divider} />
          {/* Unhealthy */}
          <div className={classes.iconWithCount} onClick={handleClick}>
            <div className={clsx(classes.iconContainer, classes.error)}>
              <FontAwesomeIcon className={classes.icon}>triangle-exclamation</FontAwesomeIcon>
            </div>
            <Text variant="body2">{statusDetails?.unhealthy}</Text>
          </div>
          <div className={classes.divider} />
          {/* Warning */}
          {statusDetails?.warning ? (
            <>
              <div className={classes.iconWithCount} onClick={handleClick}>
                <div className={clsx(classes.iconContainer, classes.warning)}>
                  <FontAwesomeIcon className={classes.icon}>triangle-exclamation</FontAwesomeIcon>
                </div>
                <Text variant="body2">{statusDetails?.warning}</Text>
              </div>
              <div className={classes.divider} />
            </>
          ) : null}
        </div>
        <div className={classes.iconWithCount}>
          {statusDetails?.unhealthy > 0 || statusDetails?.warning > 0 ? (
            <Text variant="body2">
              <b>{problematicCount}</b>{' '}
              {type === ServiceHealthHeaderType.HypervisorServices ||
              type === ServiceHealthHeaderType.ControlPlaneServices
                ? 'services'
                : 'hosts'}{' '}
              {problematicCount === 1 ? 'is' : 'are'} experiencing problems.
            </Text>
          ) : statusDetails?.healthy > 0 ? (
            <Text variant="body2">Great! Everything is online and operational.</Text>
          ) : (
            <Text variant="body2">
              No{' '}
              {type === ServiceHealthHeaderType.HypervisorServices ||
              type === ServiceHealthHeaderType.ControlPlaneServices
                ? 'services'
                : 'hosts'}{' '}
              available.
            </Text>
          )}
        </div>
      </div>
    </CardWithStatusBar>
  )
}

export default ServiceHealthHeaderCard
